import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { toast } from 'sonner';
import { CalendarBlank, CurrencyEur, ClipboardText, SignOut, MagnifyingGlass, ArrowLeft, CheckCircle } from '@phosphor-icons/react';
import { isMobileDevice } from '../utils/device';
import { ATTENDANCE_STATUS, STATUS_SOLID_COLORS } from './teacher/constants';

const DAYS = ['LUN', 'MAR', 'MER', 'GIO', 'VEN'];
const DAYS_LONG = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì'];

function fmtCurrency(v) {
  return `€ ${(v || 0).toFixed(2)}`;
}

function fmtDate(s) {
  if (!s) return '';
  try {
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${y}`;
  } catch { return s; }
}

function getCurrentAcademicYear() {
  const now = new Date();
  return now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
}

function todayJsToWeekIdx() {
  const jsDay = new Date().getDay();
  const map = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4 };
  return map[jsDay] ?? 0;
}

export default function MobilePage() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState('home');

  if (loading) return <FullscreenLoading />;
  if (!user) return <Navigate to="/login" replace />;
  // Modalità preview (?preview=1): bypassa il redirect desktop per consentire l'anteprima da admin
  const isPreview = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('preview') === '1';
  // Se non è un device mobile, reindirizza all'interfaccia desktop (a meno che non sia in preview)
  if (!isPreview && !isMobileDevice()) {
    if (user.role === 'admin' || user.role === 'collaborator' || user.role === 'other') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex flex-col">
      <MobileHeader user={user} onLogout={logout} />
      <main className="flex-1 overflow-y-auto pb-20">
        {tab === 'home' && <HomeTab onNavigate={setTab} user={user} />}
        {tab === 'lessons' && <LessonsTab onBack={() => setTab('home')} />}
        {tab === 'payments' && <PaymentsTab onBack={() => setTab('home')} />}
        {tab === 'attendance' && <AttendanceTab onBack={() => setTab('home')} />}
      </main>
      <MobileBottomNav tab={tab} setTab={setTab} />
    </div>
  );
}

function FullscreenLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
      <div className="animate-spin w-8 h-8 border-2 border-[#2D2A4A] border-t-transparent rounded-full"></div>
    </div>
  );
}

function MobileHeader({ user, onLogout }) {
  return (
    <header className="bg-[#2D2A4A] text-white px-4 pt-6 pb-5 rounded-b-3xl shadow-md">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs opacity-70 uppercase tracking-wider">Ciao</div>
          <div className="font-bold text-lg leading-tight truncate max-w-[220px]">{user.name}</div>
          {user.instrument && <div className="text-xs opacity-80 mt-0.5">{user.instrument}</div>}
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
          data-testid="mobile-logout-btn"
          aria-label="Esci"
        >
          <SignOut size={18} />
        </button>
      </div>
    </header>
  );
}

function HomeTab({ onNavigate, user }) {
  const anno = getCurrentAcademicYear();
  const cards = [
    { key: 'lessons', title: 'Orario Lezioni', subtitle: 'Calendario giorno per giorno', icon: CalendarBlank, color: 'from-indigo-500 to-purple-600' },
    { key: 'payments', title: 'Elenco Pagamenti', subtitle: 'Scadenze e importi', icon: CurrencyEur, color: 'from-emerald-500 to-teal-600' },
    { key: 'attendance', title: 'Rilevazione Presenze', subtitle: 'Segna assenti e ritardi', icon: ClipboardText, color: 'from-orange-500 to-rose-600' },
  ];
  return (
    <div className="px-4 pt-5 space-y-3">
      <div className="text-xs uppercase tracking-wider font-semibold text-[#71717A]">A.A. {anno}/{String(anno + 1).slice(-2)}</div>
      {cards.map(c => (
        <button
          key={c.key}
          type="button"
          onClick={() => onNavigate(c.key)}
          className={`w-full text-left p-5 rounded-2xl bg-gradient-to-br ${c.color} text-white shadow-md active:scale-[0.98] transition-transform`}
          data-testid={`mobile-home-${c.key}`}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
              <c.icon size={26} weight="duotone" />
            </div>
            <div className="flex-1">
              <div className="text-lg font-bold">{c.title}</div>
              <div className="text-xs opacity-90">{c.subtitle}</div>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

function MobileBottomNav({ tab, setTab }) {
  const items = [
    { key: 'home', label: 'Home', icon: CalendarBlank },
    { key: 'lessons', label: 'Orario', icon: CalendarBlank },
    { key: 'payments', label: 'Paga.', icon: CurrencyEur },
    { key: 'attendance', label: 'Pres.', icon: ClipboardText },
  ];
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E4E4E7] shadow-lg z-50" data-testid="mobile-bottom-nav">
      <div className="flex items-center justify-around px-2 py-2 safe-bottom">
        {items.map(it => (
          <button
            key={it.key}
            type="button"
            onClick={() => setTab(it.key)}
            className={`flex-1 flex flex-col items-center gap-0.5 py-1 rounded-lg transition-colors ${tab === it.key ? 'text-[#2D2A4A]' : 'text-[#A1A1AA]'}`}
            data-testid={`mobile-nav-${it.key}`}
          >
            <it.icon size={22} weight={tab === it.key ? 'fill' : 'regular'} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">{it.label}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}

/* ================= LESSONS ================= */
function LessonsTab({ onBack }) {
  const [day, setDay] = useState(todayJsToWeekIdx());
  const [loading, setLoading] = useState(false);
  const [lessons, setLessons] = useState([]);
  const anno = getCurrentAcademicYear();

  useEffect(() => {
    setLoading(true);
    api.getLessons({ anno })
      .then(res => setLessons(res.data || []))
      .catch(() => toast.error('Errore caricamento lezioni'))
      .finally(() => setLoading(false));
  }, [anno]);

  const dayLessons = useMemo(
    () => lessons.filter(l => l.day === day).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')),
    [lessons, day]
  );

  return (
    <div className="px-4 pt-4">
      <SectionHeader title="Orario Lezioni" onBack={onBack} testid="mobile-lessons-header" />
      <div className="flex items-center gap-1.5 overflow-x-auto pb-2 -mx-1 px-1" data-testid="mobile-lessons-day-tabs">
        {DAYS.map((d, i) => (
          <button
            key={d}
            type="button"
            onClick={() => setDay(i)}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider shrink-0 ${day === i ? 'bg-[#2D2A4A] text-white' : 'bg-white text-[#52525B] border border-[#E4E4E7]'}`}
            data-testid={`mobile-lessons-day-${i}`}
          >
            {d}
          </button>
        ))}
      </div>
      {loading ? <SkeletonCards /> : dayLessons.length === 0 ? (
        <EmptyState icon={CalendarBlank} text={`Nessuna lezione ${DAYS_LONG[day]}`} />
      ) : (
        <div className="space-y-2 mt-2">
          {dayLessons.map(l => (
            <div key={l.id} className="bg-white rounded-xl p-3 shadow-sm border border-[#E4E4E7]" data-testid={`mobile-lesson-${l.id}`}>
              <div className="flex items-center gap-3">
                <div className="text-[#2D2A4A] font-mono font-bold text-sm bg-[#EEF2FF] rounded-lg px-2 py-1 min-w-[80px] text-center leading-tight">
                  {l.start_time}<br /><span className="opacity-70 text-[10px]">{l.end_time}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#0A0A0A] uppercase text-sm truncate">{l.student_name}</div>
                  {l.is_prova && <span className="inline-block mt-1 px-1.5 py-0.5 bg-amber-500 text-white text-[9px] font-extrabold uppercase rounded">PROVA</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ================= PAYMENTS ================= */
function PaymentsTab({ onBack }) {
  const anno = getCurrentAcademicYear();
  const periodi = useMemo(() => [
    { key: '1', label: `15/09/${anno}`, date: `${anno}-09-15` },
    { key: '2', label: `15/12/${anno}`, date: `${anno}-12-15` },
    { key: '3', label: `15/02/${anno + 1}`, date: `${anno + 1}-02-15` },
  ], [anno]);
  const [periodo, setPeriodo] = useState('1');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_dovuto_alunno: 0, total_riscosso_alunno: 0, total_da_riscuotere_alunno: 0 });

  useEffect(() => {
    const p = periodi.find(x => x.key === periodo);
    if (!p) return;
    setLoading(true);
    api.getPayments({ anno, data_scadenza: p.date, limit: 500 })
      .then(res => { setRows(res.data.records || []); setTotals(res.data.totals || {}); })
      .catch(() => toast.error('Errore caricamento pagamenti'))
      .finally(() => setLoading(false));
  }, [anno, periodo, periodi]);

  return (
    <div className="px-4 pt-4">
      <SectionHeader title="Elenco Pagamenti" onBack={onBack} testid="mobile-payments-header" />
      <div className="flex items-center gap-1.5 overflow-x-auto pb-2 -mx-1 px-1" data-testid="mobile-payments-periodo-tabs">
        {periodi.map((p, i) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setPeriodo(p.key)}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider shrink-0 ${periodo === p.key ? 'bg-[#2D2A4A] text-white' : 'bg-white text-[#52525B] border border-[#E4E4E7]'}`}
            data-testid={`mobile-payments-periodo-${p.key}`}
          >
            Per. {i + 1} · {p.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 my-3">
        <TotalPill label="Dovuto" value={fmtCurrency(totals.total_dovuto_alunno)} color="bg-[#EEF2FF] text-[#4338CA]" />
        <TotalPill label="Riscosso" value={fmtCurrency(totals.total_riscosso_alunno)} color="bg-emerald-50 text-emerald-700" />
        <TotalPill label="Da Ric." value={fmtCurrency(totals.total_da_riscuotere_alunno)} color="bg-amber-50 text-amber-700" />
      </div>
      {loading ? <SkeletonCards /> : rows.length === 0 ? (
        <EmptyState icon={CurrencyEur} text="Nessun addebito con questa scadenza" />
      ) : (
        <div className="space-y-2 mt-1">
          {rows.map(p => {
            const paid = !!p.data_pagamento;
            return (
              <div key={p.id} className={`bg-white rounded-xl p-3 shadow-sm border ${paid ? 'border-emerald-200' : 'border-amber-200'}`} data-testid={`mobile-payment-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold uppercase text-sm text-[#0A0A0A] truncate">{p.student_name}</div>
                    <div className="text-xs text-[#71717A] truncate mt-0.5">{p.descrizione || '-'}</div>
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] font-mono">
                      <span className="text-[#71717A]">Scad {fmtDate(p.data_scadenza)}</span>
                      {paid ? <span className="text-emerald-700 font-bold">Pagato {fmtDate(p.data_pagamento)}</span> : <span className="text-amber-700 font-bold">Non pagato</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold font-['Work_Sans'] text-[#0A0A0A]">{fmtCurrency(p.importo)}</div>
                    {p.pagato_insegnante > 0 && <div className="text-[10px] text-emerald-700 font-semibold">Pagato Ins {fmtCurrency(p.pagato_insegnante)}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ================= ATTENDANCE ================= */
function AttendanceTab({ onBack }) {
  const anno = getCurrentAcademicYear();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [attendance, setAttendance] = useState({}); // by lesson_id
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const jsDay = new Date(date).getDay();
      const map = { 1: 0, 2: 1, 3: 2, 4: 3, 5: 4 };
      const dayIdx = map[jsDay];
      if (dayIdx === undefined) { setLessons([]); setAttendance({}); return; }
      const [lessonsRes, attRes] = await Promise.all([
        api.getLessons({ anno }),
        api.getAttendance({ anno, date, limit: 200 }),
      ]);
      const dayLessons = (lessonsRes.data || []).filter(l => l.day === dayIdx).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
      setLessons(dayLessons);
      const map2 = {};
      (attRes.data?.records || []).forEach(a => { if (a.lesson_id) map2[a.lesson_id] = a; });
      setAttendance(map2);
    } catch { toast.error('Errore caricamento presenze'); }
    finally { setLoading(false); }
  }, [anno, date]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (lesson, status) => {
    setSavingId(lesson.id);
    try {
      const existing = attendance[lesson.id];
      if (existing) {
        await api.updateAttendance(existing.id, { status });
        setAttendance(a => ({ ...a, [lesson.id]: { ...existing, status } }));
      } else {
        const res = await api.createAttendance({
          lesson_id: lesson.id,
          student_id: lesson.student_id,
          student_name: lesson.student_name,
          date,
          status,
        });
        setAttendance(a => ({ ...a, [lesson.id]: res.data }));
      }
      toast.success('Presenza aggiornata');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore salvataggio');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="px-4 pt-4">
      <SectionHeader title="Rilevazione Presenze" onBack={onBack} testid="mobile-attendance-header" />
      <div className="bg-white rounded-xl p-3 border border-[#E4E4E7] mb-3">
        <label className="text-[10px] uppercase tracking-wider font-semibold text-[#71717A]">Data</label>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          className="w-full mt-1 text-base font-mono outline-none"
          data-testid="mobile-attendance-date"
        />
      </div>
      {loading ? <SkeletonCards /> : lessons.length === 0 ? (
        <EmptyState icon={ClipboardText} text="Nessuna lezione in questa data" />
      ) : (
        <div className="space-y-2 mt-1">
          {lessons.map(l => {
            const att = attendance[l.id];
            const status = att?.status || '';
            return (
              <div key={l.id} className="bg-white rounded-xl p-3 shadow-sm border border-[#E4E4E7]" data-testid={`mobile-attendance-${l.id}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="text-[#2D2A4A] font-mono font-bold text-sm bg-[#EEF2FF] rounded-lg px-2 py-1 min-w-[70px] text-center leading-tight">
                    {l.start_time}<br /><span className="opacity-70 text-[10px]">{l.end_time}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold uppercase text-sm text-[#0A0A0A] truncate">{l.student_name}</div>
                    {l.is_prova && <span className="inline-block px-1.5 py-0.5 bg-amber-500 text-white text-[9px] font-extrabold uppercase rounded">PROVA</span>}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {ATTENDANCE_STATUS.map(s => (
                    <StatusBtn
                      key={s}
                      label={s}
                      active={(status || '').toLowerCase() === s.toLowerCase()}
                      solidCls={STATUS_SOLID_COLORS[s]}
                      disabled={savingId === l.id}
                      onClick={() => setStatus(l, s)}
                      testid={`mobile-att-${s.toLowerCase().replace(/\s+/g, '-')}-${l.id}`}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ================= HELPERS ================= */
function SectionHeader({ title, onBack, testid }) {
  return (
    <div className="flex items-center gap-3 mb-3" data-testid={testid}>
      <button type="button" onClick={onBack} className="w-9 h-9 rounded-full bg-white border border-[#E4E4E7] flex items-center justify-center" aria-label="Indietro">
        <ArrowLeft size={16} />
      </button>
      <h1 className="text-lg font-bold text-[#0A0A0A]">{title}</h1>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-[#E4E4E7]">
      <Icon size={40} className="mx-auto text-[#D4D4D8] mb-2" weight="duotone" />
      <p className="text-sm text-[#71717A]">{text}</p>
    </div>
  );
}

function SkeletonCards() {
  return (
    <div className="space-y-2 mt-2">
      {[0, 1, 2].map(i => (
        <div key={i} className="h-16 rounded-xl bg-white border border-[#E4E4E7] animate-pulse"></div>
      ))}
    </div>
  );
}

function TotalPill({ label, value, color }) {
  return (
    <div className={`rounded-xl p-2 text-center ${color}`}>
      <div className="text-[9px] font-bold uppercase tracking-wider opacity-90">{label}</div>
      <div className="text-xs font-bold font-['Work_Sans'] mt-0.5 truncate">{value}</div>
    </div>
  );
}

function StatusBtn({ label, active, solidCls, onClick, disabled, testid }) {
  const activeCls = solidCls || 'bg-[#2D2A4A] text-white';
  const inactiveCls = 'bg-[#F4F4F5] text-[#71717A] border border-[#E4E4E7]';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-lg py-2 px-1 text-[10px] font-bold uppercase tracking-tight leading-tight ${active ? activeCls : inactiveCls} disabled:opacity-50`}
      data-testid={testid}
    >
      {active && <CheckCircle size={11} weight="fill" className="inline mr-0.5 -mt-0.5" />}{label}
    </button>
  );
}
